import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import Button from '../common/Button';
import { Icon } from '../common/Icon';
import { ROUTES } from '../../constants/constants';
import { sddAPI } from '../../services/api';
import { isSddApplicable } from '../../utils/companyListing';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';

/**
 * "Your listed company has no UPSI mailbox" — on the dashboard, for the owner.
 *
 * Shown only to the person who can act on it: the company owner (a CA/CS with
 * admin access cannot connect the mailbox), for every company whose UPSI
 * register is open — i.e. not explicitly switched off (Ritik's call, 17 Sep:
 * the register is offered to every company, so the warning is too). Switching
 * the SDD off on a company removes the warning with it.
 *
 * Quiet on failure: if the status call fails for a company, that company is
 * simply not counted. A dashboard banner must never be the thing that errors.
 */
export default function UpsiMailboxBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { companies, selectCompany } = useApp();
  const [missing, setMissing] = useState([]);

  const userId = String(user?._id || user?.id || '');

  useEffect(() => {
    let cancelled = false;
    // The list API populates createdBy (and may populate owner), so a ref can
    // arrive as { _id, fullName } rather than an id.
    const idOf = (v) => (v && typeof v === 'object' ? String(v._id || '') : (v ? String(v) : ''));
    const owned = (companies || []).filter((c) => {
      const owner = idOf(c.owner);
      const creator = idOf(c.createdBy);
      const isOwner = userId && (owner === userId || (!owner && creator === userId));
      const sddApplies = isSddApplicable(c);
      return isOwner && sddApplies;
    }).slice(0, 12);

    if (!owned.length) {
      if (process.env.NODE_ENV !== 'production' && (companies || []).length) {
        // eslint-disable-next-line no-console
        console.info('[UPSI mailbox banner] none of your companies count as owned by you', { userId, companies: (companies || []).map((c) => ({ name: c.name, owner: c.owner, createdBy: c.createdBy, sddApplicable: c.sddApplicable })) });
      }
      setMissing([]); return undefined;
    }

    Promise.all(owned.map(async (c) => {
      try {
        const res = await sddAPI.getMailbox(c._id);
        return res?.data?.mailbox ? null : c;
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[UPSI mailbox banner] could not read mailbox status for', c.name, error?.message || error);
        }
        return null;
      }
    })).then((rows) => { if (!cancelled) setMissing(rows.filter(Boolean)); });

    return () => { cancelled = true; };
  }, [companies, userId]);

  if (!missing.length) return null;

  const one = missing.length === 1;
  const open = () => {
    if (one && typeof selectCompany === 'function') selectCompany(missing[0]);
    navigate(ROUTES.SDD);
  };

  return (
    <div
      className="flex items-center gap-3.5 px-4 py-3 mb-3 rounded-[12px] border"
      style={{ background: '#faf6eb', borderColor: '#ebdcb9', boxShadow: '0 2px 8px rgba(122,112,96,0.02)' }}
      role="status"
    >
      <Icon name="warning" size={18} style={{ color: '#8a6a1f' }} />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-bold" style={{ color: '#8a6a1f' }}>
          {one
            ? `${missing[0].name} has no UPSI mailbox connected`
            : `${missing.length} companies have no UPSI mailbox connected`}
        </div>
        <div className="text-[10.5px] text-[#7a7060] mt-[1px]">
          UPSI shared by email is not being recorded in the register automatically. Connect the
          company&apos;s <span className="font-mono">upsi@</span> mailbox once — insiders then only Cc it.
          {!one && <> ({missing.map((c) => c.name).join(', ')})</>}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="group shrink-0 whitespace-nowrap !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]"
        rightIcon={<FiChevronRight size={12} className="transition-transform duration-200 group-hover:translate-x-0.5" />}
        onClick={open}
      >
        <span className="transition-transform duration-200 group-hover:translate-x-0.5">Connect mailbox</span>
      </Button>
    </div>
  );
}
